import { useEffect } from 'react'
import { useAuth } from '../../providers/AuthProvider'

export default function JobDetailModal({ isOpen, onClose, job, onApply, isApplying }) {
    const { user } = useAuth()
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    if (!isOpen || !job) return null

    // Determine status badge
    const isClosed = job.status === 'closed'
    const statusLabel = isClosed ? '마감' : '모집중'
    const statusColor = isClosed ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600'

    // Address for Map Link (Kakao Map)
    // Fallback to location if address is missing
    const addressToSearch = String(job.address || job.location || '')
    const mapLink = `https://map.kakao.com/link/search/${encodeURIComponent(addressToSearch)}`

    // Helper to safely render content that might be object/array
    const safeRender = (value, fallback = '') => {
        if (value === null || value === undefined) return fallback
        if (typeof value === 'string' || typeof value === 'number') return value
        if (Array.isArray(value)) return value.join(', ')
        if (typeof value === 'object') return null // Prevent object crash, or JSON.stringify(value) if debugging
        return fallback
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 sm:p-0">
            <div
                className="bg-white w-full max-w-2xl h-[90vh] sm:h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-start p-6 border-b pb-4">
                    <div className="flex-1 pr-4">
                        <div className="flex gap-2 mb-2">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${statusColor}`}>
                                {statusLabel}
                            </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                            {safeRender(job.title)}
                        </h2>
                        <div className="flex items-center gap-2 mt-2 text-gray-400 text-sm">
                            <span>조회 {safeRender(job.view_count, 0)}</span>
                            <span>•</span>
                            <span>{safeRender(job.date)} 작성</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* Salary Section */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-bold text-gray-900 border-l-4 border-blue-500 pl-2">급여</h3>
                        <div className="flex items-center justify-between bg-blue-50/50 p-4 rounded-xl border border-blue-50">
                            <span className="text-lg font-bold text-gray-800">{safeRender(job.salary)}</span>
                            <span className="text-xs text-gray-500 px-2 py-1 bg-white rounded border">협의 가능</span>
                        </div>
                    </div>

                    {/* Schedule Section */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 border-l-4 border-blue-500 pl-2">근무 조건</h3>
                        <div className="grid grid-cols-[80px_1fr] gap-y-4 text-sm">
                            <span className="text-gray-500 font-medium pt-0.5">근무요일</span>
                            <div className="text-gray-800 font-medium">
                                {safeRender(job.work_days, '요일 협의')}
                            </div>

                            <span className="text-gray-500 font-medium pt-0.5">근무시간</span>
                            <div className="text-gray-800">
                                <p className="font-medium">{safeRender(job.time, '시간 협의')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Location Section */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-end">
                            <h3 className="text-sm font-bold text-gray-900 border-l-4 border-blue-500 pl-2">근무지 정보</h3>
                            {addressToSearch && (
                                <a
                                    href={mapLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs flex items-center gap-1 text-gray-500 hover:text-blue-600 transition border px-2 py-1 rounded hover:border-blue-400"
                                >
                                    <span>교통편 확인하기</span>
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                            <h4 className="font-bold text-gray-900">{safeRender(job.pharmacy_name, '약국명 비공개')}</h4>
                            {job.address && (
                                <p className="text-sm text-gray-800 font-medium">{safeRender(job.address)}</p>
                            )}
                            {(safeRender(job.location) !== safeRender(job.address)) && (
                                <p className="text-sm text-gray-600">{safeRender(job.location, '주소 정보가 없습니다.')}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2">
                                <span className="text-[10px] bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-500">처방조제</span>
                                <span className="text-[10px] bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-500">복약지도</span>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Description */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-bold text-gray-900 border-l-4 border-blue-500 pl-2">상세 내용</h3>
                        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line bg-gray-50/50 p-4 rounded-xl min-h-[100px]">
                            {safeRender(job.description)}

                            <div className="mt-6 pt-4 border-t border-dashed border-gray-200 text-gray-500 text-xs">
                                <p>* 주요업무: {safeRender(job.key_duties, '정보 없음')}</p>
                                <p className="mt-1">* 본 공고는 약사님을 구인하기 위한 정보입니다.</p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer Action */}
                <div className="p-4 border-t bg-white safe-area-bottom">
                    <button
                        onClick={() => onApply(job)}
                        disabled={isApplying || isClosed}
                        className={`w-full py-3.5 rounded-xl font-bold text-white text-base shadow-lg transition flex justify-center items-center gap-2
                            ${isClosed
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                            }`}
                    >
                        {isApplying ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>지원 처리 중...</span>
                            </>
                        ) : isClosed ? (
                            '채용 마감'
                        ) : (
                            '지금 지원하기'
                        )}
                    </button>
                    {!user && !isClosed && (
                        <p className="text-center text-xs text-blue-500 mt-2 cursor-pointer underline">
                            로그인이 필요합니다
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
